import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatPrice, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Star, Clock, BarChart3, PlayCircle, Lock, CheckCircle2, CreditCard, Smartphone, ArrowRight, Loader2, Tag, X, Wallet } from "lucide-react";
import CourseReviews from "@/components/CourseReviews";

export default function CourseDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [yapeOpen, setYapeOpen] = useState(false);
  const [loadingPay, setLoadingPay] = useState(false);
  const [loadingYape, setLoadingYape] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [culqi, setCulqi] = useState({ enabled: false, public_key: "" });
  const [loadingCulqi, setLoadingCulqi] = useState(false);
  const culqiRef = useRef(null);

  const load = () => api.get(`/courses/${slug}`).then(({ data }) => setCourse(data)).catch(() => setCourse(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug, user]);
  useEffect(() => { api.get("/payments/culqi/config").then(({ data }) => setCulqi(data)).catch(() => {}); }, []);

  const finalPrice = course ? (coupon ? +(course.price * (1 - coupon.discount_percent / 100)).toFixed(2) : course.price) : 0;

  const requireLogin = () => {
    if (!user) { toast.error("Inicia sesión para comprar"); navigate("/login"); return false; }
    return true;
  };

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: couponInput });
      setCoupon(data);
      toast.success(`Cupón aplicado: ${data.discount_percent}% de descuento`);
    } catch (e) {
      setCoupon(null);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const payStripe = async () => {
    if (!requireLogin()) return;
    setLoadingPay(true);
    try {
      const { data } = await api.post("/payments/checkout", { course_id: course.id, origin_url: window.location.origin, coupon_code: coupon?.code });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setLoadingPay(false);
    }
  };

  const openYape = () => { if (requireLogin()) setYapeOpen(true); };

  const payYape = async () => {
    setLoadingYape(true);
    try {
      await api.post("/payments/yape-simulate", { course_id: course.id, coupon_code: coupon?.code });
      toast.success("¡Pago con Yape confirmado! Ya tienes acceso.");
      setYapeOpen(false);
      navigate(`/aprender/${course.slug}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoadingYape(false); }
  };

  // Culqi real (tarjeta + Yape en soles) — requiere llaves API configuradas
  const payCulqi = () => {
    if (!requireLogin()) return;
    if (!window.CulqiCheckout) { toast.error("Culqi aún está cargando, intenta de nuevo"); return; }
    const config = {
      settings: { title: "CapacitaPro", currency: "PEN", amount: Math.round(finalPrice * 100) },
      client: { email: user.email },
      options: { lang: "es", modal: true, installments: false, paymentMethods: { tarjeta: true, yape: true, billetera: true, bancaMovil: true } },
      appearance: { theme: "default", menuType: "sidebar" },
    };
    const checkout = new window.CulqiCheckout(culqi.public_key, config);
    checkout.culqi = async () => {
      if (window.Culqi.token) {
        const tokenId = window.Culqi.token.id;
        window.Culqi.close();
        setLoadingCulqi(true);
        try {
          await api.post("/payments/culqi/charge", { token_id: tokenId, email: user.email, course_id: course.id, coupon_code: coupon?.code });
          toast.success("¡Pago aprobado! Ya tienes acceso.");
          navigate(`/aprender/${course.slug}`);
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail));
        } finally { setLoadingCulqi(false); }
      } else if (window.Culqi.error) {
        toast.error(window.Culqi.error.user_message || "No se pudo procesar el pago");
      }
    };
    culqiRef.current = checkout;
    checkout.open();
  };

  useEffect(() => {
    if (!culqi.enabled) return;
    const s = document.createElement("script");
    s.src = "https://js.culqi.com/checkout-js";
    s.async = true;
    document.body.appendChild(s);
    return () => { try { document.body.removeChild(s); } catch {} };
  }, [culqi.enabled]);

  if (course === null) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#00E5FF] animate-spin" /></div>;
  if (course === false) return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-[#8A8D98]">Curso no encontrado</div>;

  return (
    <div className="App">
      <Navbar />
      <section className="pt-24">
        {/* Header banner */}
        <div className="relative border-b border-white/10">
          <div className="absolute inset-0 overflow-hidden">
            <img src={course.image_url} alt="" className="w-full h-full object-cover opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-[#050505]/60" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <span className="font-mono text-xs uppercase tracking-widest text-[#00E5FF] border border-[#00E5FF]/30 rounded-full px-3 py-1">{course.category}</span>
              <h1 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mt-5 mb-4">{course.title}</h1>
              <p className="font-body text-lg text-[#8A8D98] max-w-2xl mb-6">{course.short_description}</p>
              <div className="flex flex-wrap items-center gap-6 font-mono text-sm text-[#8A8D98]">
                <span className="flex items-center gap-1.5"><Star className="w-4 h-4 fill-[#FFB020] text-[#FFB020]" /> {course.rating} valoración</span>
                <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {course.duration_hours} horas</span>
                <span className="flex items-center gap-1.5"><BarChart3 className="w-4 h-4" /> {course.level}</span>
                <span>{course.students_count?.toLocaleString()} alumnos</span>
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 space-y-12">
            <div>
              <h2 className="font-heading font-bold text-2xl mb-4">Descripción del curso</h2>
              <p className="font-body text-[#8A8D98] leading-relaxed">{course.description}</p>
            </div>

            <div>
              <h2 className="font-heading font-bold text-2xl mb-6">Contenido del curso</h2>
              <Accordion type="single" collapsible className="space-y-3">
                {course.syllabus?.map((mod, i) => (
                  <AccordionItem key={i} value={`m${i}`} className="bg-[#0C0D12] border border-white/10 rounded-md px-5">
                    <AccordionTrigger data-testid={`syllabus-${i}`} className="font-heading font-medium hover:no-underline hover:text-[#00E5FF]">{mod.title}</AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-2 pb-2">
                        {mod.items?.map((it, j) => (
                          <li key={j} className="flex items-center gap-2 font-body text-sm text-[#8A8D98]"><CheckCircle2 className="w-4 h-4 text-[#00E6BA]" /> {it}</li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

            <div>
              <h2 className="font-heading font-bold text-2xl mb-6">Lecciones</h2>
              <div className="space-y-2">
                {course.lessons?.map((l) => (
                  <div key={l.id} className="flex items-center justify-between bg-[#0C0D12] border border-white/5 rounded-md px-5 py-4">
                    <div className="flex items-center gap-3">
                      {course.enrolled || l.is_preview ? <PlayCircle className="w-5 h-5 text-[#00E5FF]" /> : <Lock className="w-4 h-4 text-[#525560]" />}
                      <div>
                        <p className="font-body text-sm text-white">{l.title}</p>
                        {l.is_preview && !course.enrolled && <span className="font-mono text-[10px] uppercase text-[#00E6BA]">Vista previa gratis</span>}
                      </div>
                    </div>
                    <span className="font-mono text-xs text-[#525560]">{l.duration}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-4 bg-[#0C0D12] border border-white/10 rounded-lg p-6">
              <img src={course.instructor_picture} alt={course.instructor_name} className="w-16 h-16 rounded-full object-cover" />
              <div>
                <p className="font-mono text-xs uppercase tracking-wider text-[#525560] mb-1">Instructor</p>
                <p className="font-heading font-semibold text-lg">{course.instructor_name}</p>
                <p className="font-body text-sm text-[#8A8D98]">{course.instructor_title}</p>
              </div>
            </div>

            <CourseReviews course={course} onReload={load} />
          </div>

          {/* Sticky checkout */}
          <div className="lg:col-span-4">
            <div className="sticky top-24 bg-[#0C0D12] border border-white/10 rounded-xl overflow-hidden">
              <img src={course.image_url} alt="" className="w-full h-40 object-cover" />
              <div className="p-6">
                <div className="mb-5">
                  {coupon ? (
                    <>
                      <span className="font-mono text-lg text-[#525560] line-through mr-2">{formatPrice(course.price, course.currency)}</span>
                      <span className="font-mono font-bold text-4xl text-[#00E5FF]">{formatPrice(finalPrice, course.currency)}</span>
                    </>
                  ) : (
                    <span className="font-mono font-bold text-4xl text-[#00E5FF]">{formatPrice(course.price, course.currency)}</span>
                  )}
                  <p className="font-body text-xs text-[#525560] mt-1">Pago único · Acceso de por vida</p>
                </div>

                {!course.enrolled && (
                  <div className="mb-5">
                    {coupon ? (
                      <div className="flex items-center justify-between bg-[#00E6BA]/10 border border-[#00E6BA]/30 rounded-md px-3 py-2">
                        <span className="font-mono text-xs text-[#00E6BA] flex items-center gap-2"><Tag className="w-3.5 h-3.5" /> {coupon.code} (-{coupon.discount_percent}%)</span>
                        <button data-testid="remove-coupon-btn" onClick={() => { setCoupon(null); setCouponInput(""); }} className="text-[#525560] hover:text-white"><X className="w-4 h-4" /></button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input data-testid="coupon-input" value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} placeholder="Cupón de descuento"
                          className="bg-[#13141C] border-white/10 rounded-md focus:border-[#00E5FF] focus-visible:ring-1 focus-visible:ring-[#00E5FF] text-white h-10 font-mono text-sm" />
                        <Button data-testid="apply-coupon-btn" onClick={applyCoupon} variant="outline" className="border-white/15 bg-transparent text-white hover:bg-white/5 rounded-md h-10 shrink-0">Aplicar</Button>
                      </div>
                    )}
                  </div>
                )}

                {course.enrolled ? (
                  <Button data-testid="go-to-course-btn" onClick={() => navigate(`/aprender/${course.slug}`)} className="w-full bg-[#00E5FF] text-black font-semibold hover:bg-white rounded-md h-12">
                    Ir al curso <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <div className="space-y-3">
                    {culqi.enabled ? (
                      <Button data-testid="pay-culqi-btn" onClick={payCulqi} disabled={loadingCulqi} className="w-full bg-[#00E5FF] text-black font-semibold hover:bg-white rounded-md h-12">
                        {loadingCulqi ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wallet className="w-4 h-4 mr-2" /> Pagar con tarjeta o Yape</>}
                      </Button>
                    ) : (
                      <>
                        <Button data-testid="pay-card-btn" onClick={payStripe} disabled={loadingPay} className="w-full bg-[#00E5FF] text-black font-semibold hover:bg-white rounded-md h-12">
                          {loadingPay ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-2" /> Pagar con tarjeta</>}
                        </Button>
                        <Button data-testid="pay-yape-btn" onClick={openYape} variant="outline" className="w-full border-[#00E6BA]/40 bg-[#00E6BA]/5 text-[#00E6BA] hover:bg-[#00E6BA]/10 rounded-md h-12 font-semibold">
                          <Smartphone className="w-4 h-4 mr-2" /> Pagar con Yape
                        </Button>
                      </>
                    )}
                  </div>
                )}

                <ul className="mt-6 space-y-2">
                  {["Acceso de por vida", "Certificado de finalización", "Contenido descargable", "Soporte del instructor"].map((f) => (
                    <li key={f} className="flex items-center gap-2 font-body text-sm text-[#8A8D98]"><CheckCircle2 className="w-4 h-4 text-[#00E6BA]" /> {f}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* YAPE MODAL */}
      <Dialog open={yapeOpen} onOpenChange={setYapeOpen}>
        <DialogContent className="bg-[#0C0D12] border border-[#00E6BA]/30 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-center flex items-center justify-center gap-2">
              <div className="w-8 h-8 rounded-md bg-[#00E6BA] flex items-center justify-center"><Smartphone className="w-4 h-4 text-black" /></div>
              Pago con Yape
            </DialogTitle>
            <DialogDescription className="text-center font-body text-xs text-[#525560]">Escanea y confirma tu pago de forma segura</DialogDescription>
          </DialogHeader>
          <div className="text-center py-2">
            <div className="bg-white rounded-xl p-4 w-48 h-48 mx-auto mb-4 flex items-center justify-center">
              <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=YAPE-CAPACITAPRO-DEMO" alt="QR Yape" className="w-full h-full" />
            </div>
            <p className="font-body text-sm text-[#8A8D98] mb-1">Escanea el código con tu app Yape</p>
            <p className="font-mono font-bold text-2xl text-[#00E6BA] mb-1">{formatPrice(finalPrice, course.currency)}</p>
            <p className="font-mono text-xs text-[#525560] mb-6">CapacitaPro · +51 999 888 777</p>
            <div className="bg-[#FFB020]/10 border border-[#FFB020]/30 rounded-md p-3 mb-4">
              <p className="font-mono text-[10px] uppercase tracking-wider text-[#FFB020]">Modo demostración · pago simulado</p>
            </div>
            <Button data-testid="yape-simulate-btn" onClick={payYape} disabled={loadingYape} className="w-full bg-[#00E6BA] text-black font-semibold hover:bg-white rounded-md h-11">
              {loadingYape ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simular pago realizado"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
